import type { ICoordinatePair } from "@/components/Map/Map";
import { retrieveBrowserLocale } from "@/Utils/i18n";
import type { IAddress } from "@medoma/models/addresses";
import {
  makePostalCodeMapFriendly,
  type InternalFormatPostalCode,
  type UnknownPostalCode,
} from "@medoma/models/postalCode";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { mapboxApi } from "./ApiClient";
import { generateQueryString } from "./Helpers";
import { fetchCareHub } from "./Patients";
import { routeKeys } from "./Routes";

export const geocodingSchema = z.object({
  features: z.array(
    z.object({
      geometry: z.object({
        coordinates: z.tuple([z.number(), z.number()]),
      }),
    }),
  ),
});

export async function fetchCoordinates(
  parameters: Pick<
    IAddress<
      | InternalFormatPostalCode[keyof InternalFormatPostalCode]
      | UnknownPostalCode
    >,
    "addressLine1" | "postalCode"
  > & {
    countryCode: "SE" | "CH" | "LV";
  },
): Promise<{
  longitude: number;
  latitude: number;
}> {
  const careHub = await fetchCareHub();

  const { addressLine1: address_line1, postalCode, countryCode } = parameters;
  const postcode = makePostalCodeMapFriendly(postalCode, countryCode);

  // Ignored since Mapbox often thinks that the 'place' is the municipality, not the city.
  // const place = city;

  // Ignored since 'proximity' is more dynamic.
  // ~ 50 km north, west, east, south of care hub coordinates
  // const { minLon, minLat, maxLon, maxLat } = {
  //   minLon: careHub.longitude - 1,
  //   minLat: careHub.latitude - 0.5,
  //   maxLon: careHub.longitude + 1,
  //   maxLat: careHub.latitude + 0.5,
  // };
  // const bbox = `${minLon},${minLat},${maxLon},${maxLat}`;
  const country = parameters.countryCode.toLowerCase();
  const language = retrieveBrowserLocale();
  const limit = 1;
  const proximity = `${careHub?.longitude},${careHub?.latitude}`;
  const types = "address";
  const queryString = generateQueryString({
    address_line1,
    postcode,
    // bbox,
    country,
    language,
    limit,
    // place,
    proximity,
    types,
  });

  // https://docs.mapbox.com/api/search/geocoding/#forward-geocoding-with-structured-input
  const forwardGeocodingURL = `/search/geocode/v6/forward${queryString}`;
  const response = await mapboxApi
    .get(forwardGeocodingURL)
    .json(geocodingSchema);
  // Expected response as per: https://docs.mapbox.com/api/search/geocoding/#geocoding-response-object
  const feature = response.features[0];
  if (!feature) {
    throw new Error("No geocoding result found for the given address");
  }
  const coordinates: [number, number] = feature.geometry.coordinates;
  return { longitude: coordinates[0], latitude: coordinates[1] };
}

type ISingleRouteCoordinates = ICoordinatePair[];

const coordinatePairSchema = z.tuple([z.number(), z.number()]);

const mapboxRouteSchema = z.object({
  waypoints: z.array(
    z.object({
      location: coordinatePairSchema,
    }),
  ),
  legs: z.array(
    z.object({
      duration: z.number(),
    }),
  ),
  geometry: z.object({
    coordinates: z.array(coordinatePairSchema),
  }),
});

const mapboxDirectionsSchema = z.object({
  routes: z.array(mapboxRouteSchema),
});

async function fetchDirections(coordinates: ISingleRouteCoordinates) {
  const careHub = await fetchCareHub();

  const STARTING_POINT = careHub;
  const ENDING_POINT = careHub;
  const encodedCoordinates = [STARTING_POINT, ...coordinates, ENDING_POINT]
    .map(({ longitude, latitude }) => `${longitude},${latitude}`)
    .join(";");

  const encodedURL = encodeURI(
    `/directions/v5/mapbox/driving/${encodedCoordinates}?geometries=geojson&waypoints_per_route=true`,
  );

  const parsed = await mapboxApi.get(encodedURL).json(mapboxDirectionsSchema);

  // https://docs.mapbox.com/api/navigation/directions/#response-retrieve-directions
  // An array of route objects ordered by descending recommendation rank.
  const recommendedRoute = parsed.routes[0];
  if (!recommendedRoute) {
    return null;
  }

  return {
    coordinates: recommendedRoute.geometry.coordinates,
    legDurations: recommendedRoute.legs.map((leg) =>
      Math.round(leg.duration / 60),
    ),
  };
}

export const useDirections = (coordinates: ISingleRouteCoordinates) => {
  return useQuery({
    queryKey: routeKeys.list({ coordinates }),
    queryFn: () => fetchDirections(coordinates),
    enabled: coordinates.length >= 1,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
};
